/**
 * The open/closed state of the member's hidden-letters vault.
 *
 * The unlock token lives in Redux memory only — never localStorage, IndexedDB
 * or Cache Storage — so closing or reloading the app locks the vault again,
 * and a shared device never finds it open. It also lapses on its own at
 * `expiresAt` (see useHiddenAutoLock).
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

/** The header the API reads the unlock token from (hidden.service.ts). */
export const UNLOCK_HEADER = 'x-toiyp-unlock';

export interface HiddenState {
  token: string | null;
  expiresAt: string | null;
}

const initialState: HiddenState = { token: null, expiresAt: null };

const hiddenSlice = createSlice({
  name: 'hidden',
  initialState,
  reducers: {
    unlocked(_state, action: PayloadAction<{ token: string; expiresAt: string }>) {
      return { token: action.payload.token, expiresAt: action.payload.expiresAt };
    },
    locked() {
      return initialState;
    },
  },
});

export const { unlocked, locked } = hiddenSlice.actions;
export default hiddenSlice.reducer;

/**
 * For fetchBaseQuery's `prepareHeaders`: attach the token while the vault is
 * open. Reads state structurally so the API slices need not import the store.
 */
export function withUnlockHeader(headers: Headers, getState: () => unknown): Headers {
  const token = (getState() as { hidden?: HiddenState }).hidden?.token;
  if (token) headers.set(UNLOCK_HEADER, token);
  return headers;
}
